import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import { Link } from 'react-router-dom';
import { CoInvestorContractStatus } from '@inversor-hub/types';
import { useSignMyCoInvestorContract } from '@/lib/api/investments';
import useSnackbar from '@/hooks/useSnackbar';
import { useGetMe } from '@/lib/api/me';
import { getMissingContractData, getSignErrorMessage } from '@/features/user/contract-data';

interface CoInvestorContractModalProps {
  open: boolean;
  onClose: () => void;
  investmentId: string;
  contractStatus: CoInvestorContractStatus;
  contractUrl?: string;
  hasUserSignature: boolean;
  onSignSuccess?: () => void;
}

export const CoInvestorContractModal = ({
  open,
  onClose,
  investmentId,
  contractStatus,
  contractUrl,
  hasUserSignature,
  onSignSuccess,
}: CoInvestorContractModalProps) => {
  const { show: showSnackbar } = useSnackbar();
  const { mutate: signContract, isPending: isSigning } = useSignMyCoInvestorContract();

  const handleSign = () => {
    signContract(
      { investmentId },
      {
        onSuccess: () => {
          showSnackbar({ title: 'Contrato firmado correctamente', variant: 'success' });
          onSignSuccess?.();
          onClose();
        },
        onError: (error) => {
          showSnackbar({ title: getSignErrorMessage(error), variant: 'error' });
        },
      }
    );
  };

  const handleDownload = () => {
    if (!contractUrl) {
      return;
    }

    const link = document.createElement('a');
    link.href = contractUrl;
    link.download = 'contrato-co-inversion.pdf';
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const { data: currentUser } = useGetMe();
  const missingContractData = getMissingContractData(currentUser);
  const canSign =
    contractStatus === 'PENDING_SIGNATURE' && hasUserSignature && missingContractData.length === 0;
  const missingSignature = contractStatus === 'PENDING_SIGNATURE' && !hasUserSignature;
  const missingProfileData =
    contractStatus === 'PENDING_SIGNATURE' && hasUserSignature && missingContractData.length > 0;
  const canDownload = contractStatus === 'SIGNED' && !!contractUrl;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Contrato de co-inversión</DialogTitle>
      <DialogContent dividers>
        {contractUrl ? (
          <Box sx={{ width: '100%', height: '600px' }}>
            <iframe
              src={contractUrl}
              width="100%"
              height="100%"
              style={{ border: 'none' }}
              title="Contrato de co-inversión"
            />
          </Box>
        ) : (
          <div style={{ color: 'red' }}>No hay contrato disponible.</div>
        )}
      </DialogContent>
      <DialogActions
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
        }}
      >
        <div style={{ flex: 1 }}>
          {missingSignature && (
            <>
              <span style={{ color: 'red' }}>
                Debes subir tu firma en tu perfil antes de poder firmar el contrato.{' '}
              </span>
              <Link to="/perfil" style={{ textDecoration: 'underline' }}>
                Ir a mi perfil
              </Link>
            </>
          )}

          {missingProfileData && (
            <>
              <span style={{ color: 'red' }}>
                {`Completa estos datos en tu perfil antes de poder firmar el contrato: ${missingContractData.join(', ')}. `}
              </span>
              <Link to="/perfil" style={{ textDecoration: 'underline' }}>
                Ir a mi perfil
              </Link>
            </>
          )}

          {canSign && (
            <Button variant="contained" color="success" onClick={handleSign} disabled={isSigning}>
              {isSigning ? <CircularProgress size={16} color="inherit" /> : 'Firmar contrato'}
            </Button>
          )}

          {canDownload && (
            <Button variant="contained" color="primary" onClick={handleDownload}>
              Descargar PDF
            </Button>
          )}
        </div>
        <Button onClick={onClose}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
};
